import React, { useEffect, useMemo, useRef, useState } from "react"
import { RenderPlantDetail } from "./PlantDetail"
import { getCardStyle } from "../utils/common"
import PaginationControls from "./PaginationControls"

const AutoPaginationList = ({
  filteredPlants,
  itemsPerPage,
  itemHeight,
  handleSidebarClick,
  isAutoSwitching,
  setIsIconSwitchingAndAuto,
  emitMarkerClick,
  emitStopScrollString,
  timeSwitching = 5000,
  isAltTemplate,
  solarPlants,
  initialSiteId,
  siteId,
  isContinue,
  setIsContinue,
  setIsAutoSwitching,
  searchTerm,
  onLastAutoSiteIdChange,
}) => {
  const totalItems = filteredPlants?.length || 0
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const minHeight = itemHeight * itemsPerPage

  const intervalRef = useRef(null)
  const currentPageRef = useRef(1)

  const lastAutoSiteIdRef = useRef(null)

  const hasSelectedRef = useRef(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [selectedSiteId, setSelectedSiteId] = useState(null)
  const selectedSiteIdRef = useRef(null)

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return filteredPlants.slice(start, start + itemsPerPage)
  }, [currentPage, filteredPlants, itemsPerPage])

  const autoIndexes = useMemo(() => {
    if (!Array.isArray(solarPlants)) return []

    // const idxs = solarPlants.map((p, i) => (Number(p?.goodForShow) === 1 ? i : null)).filter((i) => i !== null)

    // // nếu không có site nào goodForShow=1 thì fallback về toàn bộ
    // return idxs.length ? idxs : solarPlants.map((_, i) => i)

    return solarPlants.map((_, i) => i)
  }, [solarPlants])

  useEffect(() => {
    currentPageRef.current = currentPage
  }, [currentPage])

  const stopAutoSwitch = () => {
    clearInterval(intervalRef.current)
    intervalRef.current = null

    setIsIconSwitchingAndAuto(false)
    emitStopScrollString(false)
  }

  const selectBySolarIndex = (index, flag, isAuto = true) => {
    if (!Array.isArray(solarPlants) || solarPlants.length === 0) return
    if (index < 0 || index >= solarPlants.length) return

    const plant = solarPlants[index]
    const sid = plant?.siteId != null ? Number(plant.siteId) : null
    if (!sid) return

    // set selected siteId
    setSelectedSiteId(sid)
    selectedSiteIdRef.current = sid

    // tìm page theo filteredPlants (đúng UI đang hiển thị)
    const idxInFiltered = filteredPlants.findIndex((p) => Number(p?.siteId) === sid)
    if (idxInFiltered !== -1) {
      const page = Math.floor(idxInFiltered / itemsPerPage) + 1
      if (page !== currentPageRef.current) setCurrentPage(page)
    }

    // lưu lại "siteId đang chạy" khi auto
    if (isAutoSwitching && isAuto) {
      lastAutoSiteIdRef.current = sid
      onLastAutoSiteIdChange?.(sid)
    }
  }

  // Cleanup khi component unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  // Khi searchTerm thay đổi:
  // - Có search -> về page 1
  // - Không search -> nhảy về page chứa selectedSiteId (nếu có)
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1)
      return
    }

    const sid = selectedSiteIdRef.current
    if (sid == null) return

    const idx = filteredPlants.findIndex((p) => Number(p?.siteId) === Number(sid))
    if (idx !== -1) {
      const page = Math.floor(idx / itemsPerPage) + 1
      setCurrentPage(page)
    }
  }, [searchTerm, itemsPerPage, filteredPlants])

  // Init select theo initialSiteId
  useEffect(() => {
    if (hasSelectedRef.current) return
    if (!Array.isArray(solarPlants)) return
    if (typeof initialSiteId !== "number") return

    const index = solarPlants.findIndex((card) => Number(card.siteId) === Number(initialSiteId))
    if (index !== -1) {
      selectBySolarIndex(index, "init")
      hasSelectedRef.current = true
    }
  }, [initialSiteId, solarPlants, filteredPlants, itemsPerPage])

  // Khi Vue gửi siteId xuống => chọn đúng site
  useEffect(() => {
    if (!Array.isArray(solarPlants) || solarPlants.length === 0) return
    if (siteId == null) return

    const sid = Number(siteId)
    if (!Number.isFinite(sid) || sid <= 0) return

    const index = solarPlants.findIndex((card) => Number(card.siteId) === sid)
    if (index !== -1) {
      selectBySolarIndex(index, "siteId", false)
    } else {
      console.warn("SiteId not found:", siteId)
    }
  }, [siteId, solarPlants, filteredPlants, itemsPerPage])

  // Khi solarPlants thay đổi (filter/update list), remap theo lastAutoSiteId
  useEffect(() => {
    if (!Array.isArray(solarPlants) || solarPlants.length === 0) return

    const lastSid = lastAutoSiteIdRef.current
    if (lastSid == null) return

    const newIndex = solarPlants.findIndex((p) => Number(p?.siteId) === Number(lastSid))
    if (newIndex !== -1) {
      selectBySolarIndex(newIndex, "remap-after-filter", false)
    }
  }, [solarPlants, filteredPlants, itemsPerPage])

  const handleAutoSwitch = () => {
    // Clear existing interval trước khi tạo cái mới
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // nếu lastAutoIndex không hợp lệ (không thuộc autoIndexes) thì reset về phần tử đầu
    const normalizeIndex = (idx) => {
      if (!autoIndexes.length) return 0
      return autoIndexes.includes(idx) ? idx : autoIndexes[0]
    }

    if (isContinue && isAutoSwitching) {
      const lastSid = lastAutoSiteIdRef.current
      let index = -1

      if (lastSid != null) {
        index = solarPlants.findIndex((p) => Number(p?.siteId) === Number(lastSid))
      }

      if (index === -1) {
        const curSid = selectedSiteIdRef.current
        if (curSid != null) {
          index = solarPlants.findIndex((p) => Number(p?.siteId) === Number(curSid))
        }
      }

      if (index === -1) {
        index = autoIndexes[0] ?? 0
      }

      selectBySolarIndex(index, "resume")
      const lastPlant = solarPlants[index]
      if (lastPlant) emitMarkerClick(lastPlant, "handleAutoSwitch")

      setIsContinue(false)
      if (isAltTemplate) {
        setIsAutoSwitching(false)
        return
      }
    }

    intervalRef.current = setInterval(() => {
      if (!isAutoSwitching) return
      if (!autoIndexes.length) return

      // current index theo solarPlants từ selectedSiteId
      const currentSid = selectedSiteIdRef.current
      const curIdx = currentSid != null ? solarPlants.findIndex((p) => Number(p?.siteId) === Number(currentSid)) : -1

      const currentPos = autoIndexes.indexOf(curIdx)
      const nextPos = currentPos === -1 ? 0 : (currentPos + 1) % autoIndexes.length
      const nextIndex = autoIndexes[nextPos]

      const plant = solarPlants[nextIndex]
      if (!plant) return

      emitMarkerClick(plant, "intervalRef.current")
      selectBySolarIndex(nextIndex, "interval")
    }, timeSwitching)
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleAutoSwitch()
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoSwitching, solarPlants, isAltTemplate, autoIndexes, timeSwitching, filteredPlants, itemsPerPage])

  return (
    <>
      <div
        style={{
          minHeight: `${minHeight}px`,
          maxHeight: `${minHeight}px`,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          paddingBottom: 12,
          overflow: "hidden",
        }}>
        {paginatedItems.map((plant, index) => {
          const sid = Number(plant?.siteId)
          const isSelected = selectedSiteId != null && sid === Number(selectedSiteId)
          const cardStyle = getCardStyle(plant, isSelected)

          return (
            <div
              key={sid || `${currentPage}-${index}`}
              onClick={() => {
                stopAutoSwitch()
                handleSidebarClick(plant)

                const newSid = Number(plant?.siteId)
                setSelectedSiteId(newSid)
                selectedSiteIdRef.current = newSid
              }}
              style={{
                ...cardStyle,
                borderRadius: 12,
                padding: "8px 12px",
                cursor: "pointer",
                transition: "all 0.3s",
                minHeight: 120,
              }}>
              <RenderPlantDetail plant={plant} />
            </div>
          )
        })}
      </div>

      <style>
        {`
          .hover-page:hover {
            background-color: #dadada !important;
          }
        `}
      </style>

      {paginatedItems?.length ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          filteredPlants={filteredPlants}
          onPrev={() => {
            if (currentPage > 1) {
              setCurrentPage(currentPage - 1)
              stopAutoSwitch()
            }
          }}
          onNext={() => {
            if (currentPage < totalPages) {
              setCurrentPage(currentPage + 1)
              stopAutoSwitch()
            }
          }}
          onGoToPage={(p) => {
            stopAutoSwitch()
            if (p !== currentPage) setCurrentPage(p)
          }}
        />
      ) : null}
    </>
  )
}

export default AutoPaginationList
